import {
  API_URL,
  authenticationLoginUrl,
  TOKEN_KEY,
  TOKEN_EXPIRATION_KEY,
  USER_DATA,
  SERVICE_CONFIG,
  SELL_ORDER_LANDING,
} from "./config/variables";
import { GetCurrentUser } from "./api/users/getCurrentUser";

// --- Helper functions ---

export const setRememberMe = (remember) => {
  document.cookie = `remember_me=${remember ? "1" : "0"}; path=/; max-age=${
    remember ? 60 * 60 * 24 * 30 : 0
  }; secure; samesite=strict`;
};

export const getRememberMe = () => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "remember_me") return decodeURIComponent(value) === "1";
  }
  return false;
};

export const clearRememberMe = () => {
  document.cookie = `remember_me=; path=/; max-age=0`;
};

export const setAccessToken = (token) => {
  sessionStorage.setItem(TOKEN_KEY, token);
};

export const getAccessToken = () => {
  return sessionStorage.getItem(TOKEN_KEY);
};

export const clearAccessToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
};

export const setAccessExpiration = (expirationUnix) => {
  sessionStorage.setItem(TOKEN_EXPIRATION_KEY, expirationUnix);
};

export const getAccessExpiration = () => {
  return sessionStorage.getItem(TOKEN_EXPIRATION_KEY);
};

export const clearAccessExpiration = () => {
  sessionStorage.removeItem(TOKEN_EXPIRATION_KEY);
};

const authProvider = {
  login: ({ email, password, captcha, rememberMe = false }) => {
    return fetch(`${API_URL}/${authenticationLoginUrl}`, {
      method: "POST",
      body: JSON.stringify({
        email: email,
        password: password,
        captcha: captcha,
        rememberMe: rememberMe, // Send rememberMe flag to backend
      }),
      credentials: "include", // Include cookies in request
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(async (response) => {
        const responseBody = await response.json().catch(() => null);

        // Check if login failed
        if (!response.ok) {
          const error = new Error(
            responseBody?.message ||
              responseBody?.error?.message ||
              "Login failed. Please try again."
          );
          error.response = responseBody;
          error.status = response.status;
          throw error;
        }

        const {
          exp,
          token,
          mfa_required,
          mfa_registered,
          user_sub,
          secret_code,
          session,
          user_name,
        } = responseBody;
        if (rememberMe) {
          setRememberMe(true);
        } else {
          setRememberMe(false);
        }

        // Extract and store access token from response
        if (token) {
          setAccessToken(token);
        }
        if (exp) {
          setAccessExpiration(exp);
        }

        // Handle MFA flow based on login response
        if (mfa_required) {
          sessionStorage.setItem(
            "mfa_user_sub",
            JSON.stringify({
              mfa_registered,
              mfa_required,
              secret_code,
              session,
              user_sub,
              user_name,
            })
          );
          return { success: true, mfaData: responseBody };
        } else {
          // No MFA required - go to dashboard
          // Fetch user data with the access token
          let userData = null;
          await GetCurrentUser().then(async ({ data }) => {
            localStorage.setItem(USER_DATA, JSON.stringify(data));
            userData = data;
          });
          return { success: true, userData };
        }
      })
      .catch((error) => {
        throw new Error(error.message || "Login failed. Please try again.");
      });
  },
  mfaSetup: ({ user_sub, session, otp }) => {
    return fetch(`${API_URL}/auth/mfa-setup`, {
      method: "POST",
      body: JSON.stringify({
        user_sub: user_sub,
        totpCode: otp,
        session: session,
      }),
      credentials: "include", // Include cookies in request
      headers: new Headers({
        "Content-Type": "application/json",
        ...(getAccessToken() && {
          Authorization: `Bearer ${getAccessToken()}`,
        }),
      }),
    })
      .then(async (response) => {
        const responseBody = await response.json().catch(() => null);
        if (response.status !== 200) {
          // Pass through the exact API error response
          const error = new Error(
            responseBody?.error?.message ||
              responseBody?.message ||
              "MFA setup failed. Please try again."
          );
          error.response = responseBody; // Store the full response
          error.status = response.status;
          throw error;
        }
        sessionStorage.removeItem("mfa_user_sub");
        let userData = null;
        await GetCurrentUser().then(async ({ data }) => {
          localStorage.setItem(USER_DATA, JSON.stringify(data));
          userData = data;
        });
        return { success: true, userData };
      })
      .catch((error) => {
        // Pass through the error with full response
        throw error;
      });
  },
  mfaVerify: ({ user_sub, session, otp }) => {
    return fetch(`${API_URL}/auth/mfa-verify`, {
      method: "POST",
      body: JSON.stringify({
        user_sub: user_sub,
        totpCode: otp,
        session: session,
      }),
      credentials: "include", // Include cookies in request
      headers: new Headers({
        "Content-Type": "application/json",
        ...(getAccessToken() && {
          Authorization: `Bearer ${getAccessToken()}`,
        }),
      }),
    })
      .then(async (response) => {
        const responseBody = await response.json().catch(() => null);
        if (response.status !== 200) {
          // Pass through the exact API error response
          const error = new Error(
            responseBody?.error?.message ||
              responseBody?.message ||
              "MFA verification failed. Please try again."
          );
          error.response = responseBody; // Store the full response
          error.status = response.status;
          throw error;
        }
        const { exp, token } = responseBody;
        // Extract and store access token from response
        if (token) {
          setAccessToken(token);
        }
        if (exp) {
          setAccessExpiration(exp);
        }
        sessionStorage.removeItem("mfa_user_sub");
        let userData = null;
        await GetCurrentUser().then(async ({ data }) => {
          localStorage.setItem(USER_DATA, JSON.stringify(data));
          userData = data;
        });
        return { success: true, userData };
      })
      .catch((error) => {
        // Pass through the error with full response
        throw error;
      });
  },
  refreshToken: async () => {
    return fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include", // Send ph_rt cookie
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(async (response) => {
        const responseBody = await response.json().catch(() => null);
        if (response.status !== 200) {
          throw new Error("Failed to refresh token");
        }

        // Extract and store new access token from Authorization header
        const authHeader = response.headers.get("Authorization");
        if (authHeader) {
          const token = authHeader.replace("Bearer ", "");
          setAccessToken(token);
        }
        if (responseBody.exp) {
          setAccessExpiration(responseBody.exp);
        }
        return responseBody;
      })
      .then(async (data) => {
        // ph_rt refresh token cookie is automatically updated by backend
        return data;
      })
      .catch(() => {
        clearAccessToken();
        authProvider.logout();
        throw new Error("Network error");
      });
  },
  checkAuth: async () => {
    // Check if access token exists in memory
    if (getAccessToken() && getAccessExpiration() > Date.now() / 1000) {
      return Promise.resolve();
    }

    // logout user if no valid token found
    await authProvider.logout();
    return Promise.reject();
  },
  getCurrentUser: async () => {
    let current_user = localStorage.getItem(USER_DATA);
    if (current_user) {
      return Promise.resolve(JSON.parse(current_user));
    } else {
      return Promise.reject();
    }
  },
  logout: () => {
    // Call backend logout endpoint to clear cookies
    const token = getAccessToken();
    fetch(`${API_URL}/auth/logout`, {
      method: "POST",
      credentials: "include", // Send cookies to be cleared
      headers: new Headers({
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      }),
    }).catch(() => {
      // Continue even if request fails
    });

    // Clear access token from memory
    clearAccessToken();
    clearRememberMe();
    clearAccessExpiration();

    // Clear local storage
    localStorage.removeItem(USER_DATA);
    localStorage.removeItem(SERVICE_CONFIG);
    localStorage.removeItem(SELL_ORDER_LANDING);

    return Promise.resolve();
  },
};

export default authProvider;
